import { useState } from "react";
import { ArrowRight, ChevronRight, Search, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { SiteFooter } from "@/components/landing/SiteFooter";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

type Category = "all" | "devices" | "firmware" | "solutions" | "management" | "troubleshooting";

type FaqItem = {
  category: Exclude<Category, "all">;
  question: string;
  answer: string;
  linkPath?: string;
};

const CATEGORY_DOT: Record<Category, string> = {
  all: "",
  devices: "bg-sky-500",
  firmware: "bg-violet-500",
  solutions: "bg-emerald-500",
  management: "bg-primary",
  troubleshooting: "bg-amber-500",
};

export function FaqPage() {
  const { messages, site, href } = useSite();
  const [selected, setSelected] = useState<Category>("all");
  const [query, setQuery] = useState("");
  const home = localizeUrl("/", site.locale, href);
  const contactSupport = localizeUrl("/contacts?inquiry=support#inquiry-form", site.locale, href);

  const categories: Array<{ id: Category; label: string }> = [
    { id: "all", label: messages.faqCategoryAll },
    { id: "devices", label: messages.faqCategoryDevices },
    { id: "firmware", label: messages.faqCategoryFirmware },
    { id: "solutions", label: messages.faqCategorySolutions },
    { id: "management", label: messages.faqCategoryManagement },
    { id: "troubleshooting", label: messages.faqCategoryTroubleshooting },
  ];

  const faqs: FaqItem[] = [
    {
      category: "devices",
      question: messages.faqQuestionCompare,
      answer: messages.faqAnswerCompare,
      linkPath: "/products",
    },
    {
      category: "devices",
      question: messages.faqQuestionHdmi,
      answer: messages.faqAnswerHdmi,
    },
    {
      category: "devices",
      question: messages.faqQuestionResolution,
      answer: messages.faqAnswerResolution,
    },
    {
      category: "devices",
      question: messages.faqQuestionAndroid,
      answer: messages.faqAnswerAndroid,
    },
    {
      category: "devices",
      question: messages.faqQuestionRemote,
      answer: messages.faqAnswerRemote,
    },
    {
      category: "firmware",
      question: messages.faqQuestionFirmwares,
      answer: messages.faqAnswerFirmwares,
    },
    {
      category: "firmware",
      question: messages.faqQuestionFirmwareCheck,
      answer: messages.faqAnswerFirmwareCheck,
    },
    {
      category: "firmware",
      question: messages.faqQuestionOtaFleet,
      answer: messages.faqAnswerOtaFleet,
      linkPath: "/solutions/ota-updates",
    },
    {
      category: "solutions",
      question: messages.faqQuestionIptvMiddleware,
      answer: messages.faqAnswerIptvMiddleware,
      linkPath: "/solutions/iptv-app",
    },
    {
      category: "solutions",
      question: messages.faqQuestionWhiteLabel,
      answer: messages.faqAnswerWhiteLabel,
      linkPath: "/solutions/customization",
    },
    {
      category: "solutions",
      question: messages.faqQuestionRemoteApp,
      answer: messages.faqAnswerRemoteApp,
      linkPath: "/solutions/inext-remote",
    },
    {
      category: "solutions",
      question: messages.faqQuestionBuy,
      answer: messages.faqAnswerBuy,
      linkPath: "/distributors",
    },
    {
      category: "management",
      question: messages.faqQuestionAlcatraz,
      answer: messages.faqAnswerAlcatraz,
      linkPath: "/solutions/alcatraz-dms",
    },
    {
      category: "management",
      question: messages.faqQuestionAlcatrazModels,
      answer: messages.faqAnswerAlcatrazModels,
    },
    {
      category: "management",
      question: messages.faqQuestionAlcatrazDeploy,
      answer: messages.faqAnswerAlcatrazDeploy,
      linkPath: "/solutions/alcatraz-dms",
    },
    {
      category: "management",
      question: messages.faqQuestionAlcatrazIntegrate,
      answer: messages.faqAnswerAlcatrazIntegrate,
      linkPath: "/solutions/alcatraz-dms",
    },
    {
      category: "management",
      question: messages.faqQuestionTr069,
      answer: messages.faqAnswerTr069,
    },
    {
      category: "troubleshooting",
      question: messages.faqQuestionBlackScreen,
      answer: messages.faqAnswerBlackScreen,
    },
    {
      category: "troubleshooting",
      question: messages.faqQuestionWifi,
      answer: messages.faqAnswerWifi,
    },
    {
      category: "troubleshooting",
      question: messages.faqQuestionBlocked,
      answer: messages.faqAnswerBlocked,
    },
  ];

  const normalizedQuery = query.trim().toLowerCase();
  const visible = faqs.filter((f) => {
    const matchesCategory = selected === "all" || f.category === selected;
    if (!matchesCategory) return false;
    if (!normalizedQuery) return true;
    return (
      f.question.toLowerCase().includes(normalizedQuery) ||
      f.answer.toLowerCase().includes(normalizedQuery)
    );
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="border-b border-border/60 bg-background/60">
          <nav
            aria-label={messages.breadcrumbLabel}
            className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
          >
            <a href={home} className="hover:text-foreground">
              {messages.tv6UltraBreadcrumbHome}
            </a>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground">{messages.navFaq}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border/60 bg-muted/10">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-20"
          />
          <div className="mx-auto max-w-3xl px-6 py-16 lg:py-20">
            <p className="text-sm font-medium text-primary animate-fade-up">
              {messages.faqEyebrow}
            </p>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl animate-fade-up">
              {messages.faqHeading}
            </h1>
            <p className="mt-4 text-base leading-7 text-muted-foreground animate-fade-up">
              {messages.faqIntro}
            </p>

            {/* Search input */}
            <div className="relative mt-8 animate-fade-up">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={messages.faqSearchPlaceholder}
                className="h-11 w-full rounded-xl border border-border bg-card pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground/60 hover:text-foreground"
                  aria-label={messages.faqSearchClear}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category filter chips */}
            <div className="mt-3 flex flex-wrap gap-2 animate-fade-up">
              {categories.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelected(id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    selected === id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {id !== "all" && (
                    <span className={`h-1.5 w-1.5 rounded-full ${CATEGORY_DOT[id]}`} aria-hidden />
                  )}
                  {label}
                  <span
                    className={`tabular-nums text-xs ${
                      selected === id ? "text-primary-foreground/80" : "text-muted-foreground"
                    }`}
                  >
                    {id === "all" ? faqs.length : faqs.filter((f) => f.category === id).length}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ accordion */}
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-6 py-12">
            <div
              key={`${selected}-${normalizedQuery}`}
              className="animate-fade-up"
              style={{ animationDelay: "120ms" }}
            >
              {visible.length === 0 ? (
                <div className="py-16 text-center">
                  <p className="text-base font-medium text-foreground">
                    {messages.faqNoResultsTitle}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {normalizedQuery
                      ? messages.faqNoResultsWithQuery.replace("{query}", query)
                      : messages.faqNoResultsCategory}
                  </p>
                  {normalizedQuery && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="mt-4 text-sm text-primary underline-offset-4 hover:underline"
                    >
                      {messages.faqSearchClear}
                    </button>
                  )}
                </div>
              ) : (
                <Accordion type="single" collapsible className="border-t border-border/60">
                  {visible.map((faq, index) => (
                    <AccordionItem
                      key={`${faq.category}-${index}`}
                      value={`item-${faq.category}-${index}`}
                      className="border-b border-border/60"
                    >
                      <AccordionTrigger
                        headingLevel={2}
                        className="group py-5 text-left text-base font-medium text-foreground hover:no-underline [&>svg]:shrink-0"
                      >
                        <span className="flex items-start gap-3">
                          <span
                            className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${CATEGORY_DOT[faq.category]}`}
                            aria-hidden
                          />
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-6 pl-[1.625rem] text-base leading-7 text-muted-foreground">
                        <div className="space-y-4">
                          <p>{faq.answer}</p>
                          {faq.linkPath ? (
                            <a
                              href={localizeUrl(faq.linkPath, site.locale, href)}
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                            >
                              {messages.learnMore}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </a>
                          ) : null}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section>
          <div className="mx-auto max-w-3xl px-6 py-16">
            <div className="flex flex-col items-start gap-5 rounded-2xl border border-border bg-card/60 p-8 sm:flex-row sm:items-center sm:justify-between animate-fade-up">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">{messages.faqContactTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{messages.faqContactBody}</p>
              </div>
              <Button asChild size="lg" className="h-10 shrink-0 rounded-lg px-5 font-medium">
                <a href={contactSupport}>
                  {messages.faqContactCta}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
